import { methodLabel } from './metrics-series.js';
import { formatPct, isProductMetric, type ProductMetricKey } from './product-metric.js';

/**
 * What each of the three product metrics counts, as the hub writes it under the figure.
 *
 * Said the way Virtual Profiler computes them (`videoIncoherenceMetrics.ts`), per video: a video with
 * incoherences is detected when a flagged idea touches any of its marked stretches, and a video
 * without incoherences is a false alarm with a flagged idea anywhere. The two sides are not symmetric, which is exactly what a
 * reader has to know before trusting a figure.
 */
export const METRIC_DEFINITIONS: Readonly<Record<ProductMetricKey, string>> = Object.freeze({
  incoherences_detected_pct: 'De los vídeos con mentira, los detectados en su tramo.',
  false_positives_pct: 'De los vídeos sinceros, los que tienen alguna idea marcada.',
  correct_pct: 'De los vídeos etiquetados, los acertados: mentira detectada o sinceros sin marcas.',
});

/**
 * «Métricas globales» (product owner, 2026-09-27), switched on in the hub's options: every figure
 * over the whole dataset, as it will be in production, where nobody knows which videos lie. A
 * flagged idea wholly outside any lie window is a false alarm wherever it is, so a lie video can
 * miss its lie and raise a false alarm at once.
 */
export const GLOBAL_METRICS = 'Métricas globales';

/** Where each figure is read from under the global metrics: upstream's own, never computed here. */
const GLOBAL_FIELDS: Readonly<Record<string, string>> = Object.freeze({
  incoherences_detected_pct: 'incoherences_detected_all_videos_pct',
  false_positives_pct: 'false_positives_all_videos_pct',
});

export const LIES_DETECTED_ALL_VIDEOS = 'incoherences_detected_all_videos_pct';
export const FALSE_POSITIVES_ALL_VIDEOS = 'false_positives_all_videos_pct';

/** Over every video: the counting rule under them says so once, not each line. */
const GLOBAL_DEFINITIONS: Readonly<Record<ProductMetricKey, string>> = Object.freeze({
  incoherences_detected_pct: 'Vídeos con la mentira detectada en su tramo.',
  false_positives_pct: 'Vídeos con alguna idea marcada fuera del tramo de mentira.',
  correct_pct: 'Vídeos acertados: mentira detectada o sinceros sin marcas.',
});

/** The field a metric's figure is read from: correct videos are over every video already. */
export function figureField(metricKey: string, global: boolean): string {
  return global ? (GLOBAL_FIELDS[metricKey] ?? metricKey) : metricKey;
}

/** What a metric counts under the reading in force; null for a key that is not a product metric. */
export function metricDefinition(metricKey: string, global: boolean): string | null {
  if (!isProductMetric(metricKey)) return null;

  return (global ? GLOBAL_DEFINITIONS : METRIC_DEFINITIONS)[metricKey];
}

/** When an idea is flagged, and what the lie stretch forgives: the rule behind the three figures. */
export function countingRule(method: string, thresholdPct: number | null, global: boolean): string {
  const line = thresholdPct === null ? 'umbral' : formatPct(thresholdPct);
  const flagged = `Una idea se marca si su veracidad (${methodLabel(method)}) baja del ${line}.`;

  return global
    ? `${GLOBAL_METRICS}, sobre todos los vídeos. ${flagged}`
    : `${flagged} En vídeos con mentira, las marcas fuera del tramo no cuentan.`;
}
